import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faPlus } from "@fortawesome/free-solid-svg-icons";
import { merge } from "lodash";
import { ConditionSetType } from "../types/types";
import { validationErrors } from "../constants/validationErrors";
import {
  ConditionSetDBOptions,
  MultipleConditionsExplanations,
} from "../constants/form-constants";
import ConditionSetHelp from "./ConditionSetHelp";
import HelperText from "../../../components/atoms/HelperText";
import {
  Input,
  FormGroup,
  InputHelperText,
  Select,
  Label,
} from "../../../components/atoms/Form";

const ConditionSet = (props) => {
  const {
    onChangeConditionSet,
    defaults,
    handleBlur,
    errors,
    touched,
    userCanSubmit,
    setTouched,
    setErrors,
  } = props;
  const minimumRequiredForMultipleConditionExplanation = 1;

  const [conditionSet, setConditionSet] = useState({
    condition: [
      {
        db: "",
        name: "",
        id: "",
      },
    ],
    multipleConditionExplanation: "",
  });
  const handleDropdownChange = (e) => {
    setConditionSet((prevState) => ({
      ...prevState,
      multipleConditionExplanation: e.target.value,
    }));
    const newErrors = { ...errors };
    const newTouched = { ...touched };
    errors.conditionSet.multipleConditionExplanation = "";
    touched.conditionSet.multipleConditionExplanation = false;
    setTouched(newTouched);
    setErrors(newErrors);
  };
  const handleAddClick = () => {
    setConditionSet({
      ...conditionSet,
      condition: [...conditionSet.condition, { db: "", name: "", id: "" }],
    });
    const newErrors = { ...errors };
    newErrors.conditionSet.condition.push({
      db: "",
      name: "",
      id: "",
    });
    const newTouched = { ...touched };
    newTouched.conditionSet.condition.push({
      db: false,
      name: false,
      id: false,
    });
    const list = [...conditionSet.condition];
    if (list.length === minimumRequiredForMultipleConditionExplanation) {
      errors.conditionSet.multipleConditionExplanation =
        "This is required for multiple conditions";
      touched.conditionSet.multipleConditionExplanation = false;
      setErrors(newErrors);
    }
    setTouched(newTouched);
  };
  const handleRemoveClick = (index) => {
    const list = [...conditionSet.condition];
    list.splice(index, 1);

    if (list.length <= minimumRequiredForMultipleConditionExplanation) {
      setConditionSet({
        ...conditionSet,
        condition: list,
        multipleConditionExplanation: "",
      });
      const newErrors = { ...errors };
      const newTouched = { ...touched };
      errors.conditionSet.multipleConditionExplanation = "";
      touched.conditionSet.multipleConditionExplanation = false;
      setTouched(newTouched);
      setErrors(newErrors);
    } else {
      setConditionSet({ ...conditionSet, condition: list });
    }
    console.log(conditionSet);
    const newErrors = { ...errors };
    newErrors.conditionSet.condition.splice(index, 1);
    setErrors(newErrors);
    const newTouched = { ...touched };
    newTouched.conditionSet.condition.splice(index, 1);
    setTouched(newTouched);
  };

  const handleInputChange = (e, index) => {
    const { name, value } = e.target;
    const list = [...conditionSet.condition];
    list[index][name] = value;
    setConditionSet({
      ...conditionSet,
      condition: list,
    });
  };
  const onValueChange = (e, index = null) => {
    const { name, value } = e.target;
    handleInputChange(e, index);

    // The code after this point is used to debug errors between user selections. If
    // the user selects db or id name error should be removed. If user selects name db
    // and id errors should be removed. If we get an empty value from the conditionSet
    // fields we check to restore any errors. For example if user removes id text we check
    // if db is not selected. If not we restore the error for the name field etc...
    if (value === "") {
      if (name === "db" && conditionSet.condition[index].id === "") {
        const newErrors = { ...errors };
        newErrors.conditionSet.condition[index].name =
          validationErrors.emptyStringError;
        setErrors(newErrors);
      } else if (name === "id" && conditionSet.condition[index].db === "") {
        const newErrors = { ...errors };
        newErrors.conditionSet.condition[index].name =
          validationErrors.emptyStringError;
        setErrors(newErrors);
      } else if (
        name === "name" &&
        conditionSet.condition[index].db === "" &&
        conditionSet.condition[index].id === ""
      ) {
        const newErrors = { ...errors };
        newErrors.conditionSet.condition[index].db =
          validationErrors.emptyStringError;
        newErrors.conditionSet.condition[index].id =
          validationErrors.emptyStringError;
        setErrors(newErrors);
      }
    }
    if (
      (name === "db" || name === "id") &&
      value !== "" &&
      errors.conditionSet.condition[index].name !== ""
    ) {
      const newErrors = { ...errors };
      newErrors.conditionSet.condition[index].name = "";
      setErrors(newErrors);
    } else if (
      name === "name" &&
      value !== "" &&
      (errors.conditionSet.condition[index].id !== "" ||
        errors.conditionSet.condition[index].db !== "")
    ) {
      const newErrors = { ...errors };
      newErrors.conditionSet.condition[index].id = "";
      newErrors.conditionSet.condition[index].db = "";
      setErrors(newErrors);
    }
  };

  useEffect(() => {
    if (defaults) {
      const newState = { ...conditionSet };
      merge(newState, defaults);
      setConditionSet(newState);
    }
  }, []);

  useEffect(() => {
    onChangeConditionSet({ ...conditionSet });
  }, [conditionSet]);

  return (
    <div className="tw-pt-8 tw-pb-8">
      <div className="tw-text-2xl">Condition(s) Set</div>
      <div className="tw-text-sm">
        The condition for which the variant is interpreted. The condition must
        be provided as either a database identifier or a name, but not both.{" "}
        <HelperText RenderContent={() => <ConditionSetHelp />} />
      </div>
      <div className="tw-pt-6 tw-px-2 tw-flex tw-w-11/12">
        <div className="tw-w-4/12 tw-mr-4">
          <Label text="Database" isRequired={true} />
        </div>
        <div className="tw-w-4/12 tw-mr-4">
          <Label text="Id" isRequired={true} />
        </div>
        <div className="tw-w-4/12 tw-mr-4">
          <Label text="Name" isRequired={true} />
        </div>
      </div>
      {conditionSet.condition.map((x, index) => {
        const key = `${index}-${conditionSet.condition}`;
        return (
          <div key={key}>
            <div className="tw-flex tw-flex-wrap tw-px-2 tw-py-4 tw-bg-gray-100">
              <div className="tw-flex tw-w-11/12">
                <FormGroup className="tw-w-4/12 tw-mr-4" hasPadding={false}>
                  <Select
                    type="select"
                    disabled={x.name}
                    name="db"
                    onBlur={(e) => handleBlur(e, index)}
                    id={`conditionSetDb${index}`}
                    onChange={(e) => onValueChange(e, index)}
                    value={x.db}
                  >
                    <option value="" />
                    {ConditionSetDBOptions.map((value) => (
                      <option
                        key={`${value}-${conditionSet.condition}`}
                        value={value}
                      >
                        {value}
                      </option>
                    ))}
                  </Select>
                  {errors.conditionSet?.condition[index]?.db &&
                  touched.conditionSet?.condition[index]?.db ? (
                    <InputHelperText type="error">
                      {validationErrors.emptyStringError}
                    </InputHelperText>
                  ) : null}
                </FormGroup>
                <FormGroup className="tw-w-4/12 tw-mr-4" hasPadding={false}>
                  <Input
                    disabled={x.name}
                    type="text"
                    name="id"
                    onBlur={(e) => handleBlur(e, index)}
                    id={`conditionSetId${index}`}
                    value={x.id}
                    onChange={(e) => onValueChange(e, index)}
                  />
                  {errors.conditionSet?.condition[index]?.id &&
                  touched.conditionSet?.condition[index]?.id ? (
                    <InputHelperText type="error">
                      {errors.conditionSet?.condition[index]?.id}
                    </InputHelperText>
                  ) : null}
                </FormGroup>
                <FormGroup className="tw-w-4/12 tw-mr-4" hasPadding={false}>
                  <Input
                    disabled={x.db || x.id}
                    type="text"
                    id={`conditionSetName${index}`}
                    name="name"
                    onBlur={(e) => handleBlur(e, index)}
                    value={x.name}
                    onChange={(e) => onValueChange(e, index)}
                  />
                  {errors.conditionSet?.condition[index]?.name &&
                  touched.conditionSet?.condition[index]?.name &&
                  conditionSet.condition[index]?.db === "" &&
                  conditionSet.condition[index]?.id === "" ? (
                    <InputHelperText type="error">
                      {validationErrors.emptyStringError}
                    </InputHelperText>
                  ) : (
                    <React.Fragment />
                  )}
                </FormGroup>
              </div>
              <div className="tw-flex tw-align-middle tw-items-center tw-justify-center tw-w-1/12">
                {conditionSet.condition.length !== 1 && (
                  <FontAwesomeIcon
                    icon={faTrash}
                    onClick={() => handleRemoveClick(index)}
                    size="lg"
                    fixedWidth={true}
                    className="tw-px-1 tw-text-red-600 tw-cursor-pointer"
                  />
                )}
              </div>
            </div>
            {(touched.conditionSet.condition[index].name ||
              touched.conditionSet.condition[index].id ||
              touched.conditionSet.condition[index].db) &&
            conditionSet.condition[index].name === "" &&
            conditionSet.condition[index].id === "" &&
            conditionSet.condition[index].db === "" &&
            !userCanSubmit ? (
              <InputHelperText type="error">
                Condition Set is a required section. The above fields should not
                be empty.
              </InputHelperText>
            ) : null}
            {conditionSet.condition.length - 1 === index && (
              <div className="tw-flex tw-m-4 tw-justify-center">
                <button
                  type="button"
                  onClick={handleAddClick}
                  disabled={
                    (conditionSet.condition[conditionSet.condition.length - 1]
                      .db === "" ||
                      conditionSet.condition[conditionSet.condition.length - 1]
                        .id === "") &&
                    conditionSet.condition[conditionSet.condition.length - 1]
                      .name === ""
                  }
                  className="btn btn-success"
                >
                  <FontAwesomeIcon
                    icon={faPlus}
                    fixedWidth={true}
                    className="tw-px-1 tw-text-white-100"
                  />
                  Add Condition
                </button>
              </div>
            )}
          </div>
        );
      })}
      <FormGroup className="tw-w-4/12 tw-mr-4" hasPadding={false}>
        {conditionSet.condition.length > 1 && (
          <div className="tw-mt-4">
            <Label text="Multiple Condition Explanation" isRequired={true} />
            <Select
              name="dropdown"
              value={conditionSet.multipleConditionExplanation}
              onChange={handleDropdownChange}
              required={true}
            >
              <option value="">Select an option</option>
              {MultipleConditionsExplanations.map((choice) => (
                <option key={choice} value={choice}>
                  {choice}
                </option>
              ))}
            </Select>
            {touched.conditionSet.multipleConditionExplanation &&
              errors.conditionSet.multipleConditionExplanation && (
                <InputHelperText type="error">
                  {errors.conditionSet.multipleConditionExplanation}
                </InputHelperText>
              )}
          </div>
        )}
      </FormGroup>
    </div>
  );
};

ConditionSet.propTypes = {
  onChangeConditionSet: PropTypes.func.isRequired,
  defaults: PropTypes.shape({
    conditionSet: ConditionSetType,
  }),
  handleBlur: PropTypes.func.isRequired,
  errors: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({}),
      PropTypes.arrayOf(PropTypes.shape({})),
    ])
  ).isRequired,
  touched: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.shape({}),
      PropTypes.arrayOf(PropTypes.shape({})),
    ])
  ).isRequired,
  setTouched: PropTypes.func.isRequired,
  setErrors: PropTypes.func.isRequired,
  userCanSubmit: PropTypes.bool,
};

ConditionSet.defaultProps = {
  defaults: null,
  userCanSubmit: true,
};

export default ConditionSet;
